import React from 'react'
import { connect } from 'react-redux'
import { CSSTransition } from 'react-transition-group'

import { ProjectIdentity, RootStateTypeExtra } from '@store/types'

import { selectFromResult, useGetBuildingQuery } from '@api/building'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import { useCompareProperty } from '@adUtilities/components/compare-property/utils'
import { PropertyType } from '@adUtilities/types'

import ComparablePropertyItem from './comparable-property-item'
import Controls from './controls'

interface ComparePropertyProps {
  propertyType: PropertyType
  projectIdentity: ProjectIdentity
  comparePropertyProps: ReturnType<typeof useCompareProperty>
  fullScreenMode: boolean
  onFullScreenToggle: (status: boolean) => void
  triggerComparisonStop: () => Promise<void>
}

function CompareProperty({
  propertyType,
  projectIdentity,
  comparePropertyProps,
  fullScreenMode,
  onFullScreenToggle,
  triggerComparisonStop,
}: ComparePropertyProps) {
  const {
    compareModeIsActive,
    comparableProperties,
    initialPropertyAtComparisonStart,
  } = comparePropertyProps
  const [isTransitioning, setIsTransitioning] = React.useState<boolean>(false)

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const { levels } = useGetBuildingQuery(
    { projectName: projectIdentity.projectId },
    { selectFromResult }
  )

  const handleTransitionState = React.useCallback(
    (state: boolean) => () => {
      setIsTransitioning(state)
    },
    []
  )

  const setLastActiveUnit = React.useCallback(() => {
    if (initialPropertyAtComparisonStart?.propertyType !== PropertyType.Unit) {
      return
    }
    const flattenedLevels = levels.flatMap((item) => item.data)
    const blockId = flattenedLevels.find(
      (item) => item.name === initialPropertyAtComparisonStart.data.name
    )?.blockId

    if (!blockId) {
      return
    }

    firebaseControlQuery.update({
      [`building.activeBlock`]: blockId,
      [`building.activeLevel`]:
        initialPropertyAtComparisonStart.references.levelId,
      [`building.activeUnit`]: initialPropertyAtComparisonStart.data.name,
    })
  }, [initialPropertyAtComparisonStart, levels])

  const setLastActiveLot = React.useCallback(() => {
    if (initialPropertyAtComparisonStart?.propertyType !== PropertyType.Lot) {
      return
    }
    firebaseControlQuery.update({
      [`houseAndLand.activePrecinctId`]:
        initialPropertyAtComparisonStart.references.precinctId,
      [`houseAndLand.activeStageId`]:
        initialPropertyAtComparisonStart.references.stageId,
      [`houseAndLand.activeLotId`]: initialPropertyAtComparisonStart.data.name,
    })
  }, [initialPropertyAtComparisonStart])

  const handleStopComparison = React.useCallback(() => {
    triggerComparisonStop()
    if (initialPropertyAtComparisonStart?.propertyType === PropertyType.Unit) {
      setLastActiveUnit()
    }
    if (initialPropertyAtComparisonStart?.propertyType === PropertyType.Lot) {
      setLastActiveLot()
    }
  }, [
    triggerComparisonStop,
    setLastActiveUnit,
    setLastActiveLot,
    initialPropertyAtComparisonStart?.propertyType,
  ])

  if (!compareModeIsActive) {
    return null
  }

  return (
    <div className="relative flex h-full w-full">
      <div className="absolute top-0 z-5 w-full items-start p-4">
        <Controls
          propertyType={propertyType}
          fullscreen={fullScreenMode}
          onFullScreenToggle={onFullScreenToggle}
          onStopComparison={handleStopComparison}
        />
      </div>
      <div className="flex w-full bg-white">
        {comparableProperties.map((item) => (
          <CSSTransition
            key={item.index}
            classNames={`basis-1/2 ${
              item.index === 0
                ? 'transition-property-compare-v2-first-item'
                : 'transition-property-compare-v2-last-item'
            }`}
            timeout={500}
            in
            appear
            mountOnEnter
            unmountOnExit
            onEnter={handleTransitionState(true)}
            onEntered={handleTransitionState(false)}
            onExit={handleTransitionState(true)}
            onExited={handleTransitionState(false)}
          >
            <div className="h-full">
              <CSSTransition
                key={`item_${item.index}`}
                classNames={
                  isTransitioning ? '' : 'transition-property-compare-v2-select'
                }
                in={!!item.property}
                timeout={300}
              >
                <div
                  className={`h-full w-full ${
                    isTransitioning ? 'opacity-100' : ''
                  }`}
                >
                  <ComparablePropertyItem
                    comparableProperty={item}
                    fullScreenMode={fullScreenMode}
                  />
                </div>
              </CSSTransition>
            </div>
          </CSSTransition>
        ))}
      </div>
    </div>
  )
}

export default connect(({ projectIdentity }: RootStateTypeExtra) => ({
  projectIdentity,
}))(CompareProperty)
